/**
 * ガチャ抽選(docs/02_game_design.md §7.2)— 純関数。
 *
 * MVP はローカルモードでクライアント抽選するが、確率仕様は
 * `functions/src/gacha.ts`(将来の callable `pullGacha`)と**同一**にしてある:
 *
 *  - レアリティを先に引き、そのレアリティ内で weight による重み付き抽選
 *  - 1回の抽選はちょうど 2 回 rng() を消費する(1回目=レアリティ、2回目=プール内)
 *  - プールに存在しないレアリティは候補から外して排出率を正規化する
 *  - 連ガチャは `guarantee.minRarity` 以上が1枠も出なければ最後の枠を保証枠に差し替える
 *  - 重複は `duplicateConvertCoins` でコインに自動変換(同一ガチャ内の重複も対象)
 *
 * 排出率・価格・変換額はすべてカタログデータ(GachaDefinition)から読む。
 * 乱数は引数で注入する(テストは決定的な rng を渡す)。
 */

import {
  RARITY_ORDER,
  type CatalogItem,
  type GachaDefinition,
  type GachaPoolEntry,
  type Rarity,
} from '../services/catalog';

/**
 * 月替わり限定枠の「当月」(`YYYY-MM`)。区切りは JST(UTC+9)。
 *
 * **本当の判定はサーバー**(`functions/src/gacha.ts` の `gachaMonth`)。
 * ここは「こんげつの げんてい」の表示と排出率の内訳をそろえるための写しで、
 * 端末の時計が多少ずれていても、出るかどうかの結論は変わらない。
 */
export const GACHA_MONTH_OFFSET_MINUTES = 540;

export function gachaMonth(now: Date, offsetMinutes: number = GACHA_MONTH_OFFSET_MINUTES): string {
  return new Date(now.getTime() + offsetMinutes * 60_000).toISOString().slice(0, 7);
}

/** その枠が当月に出るか。`months` が無い枠は恒常(いつでも出る)。 */
export function isEntryInMonth(entry: GachaPoolEntry, month: string): boolean {
  return entry.months === undefined || entry.months.includes(month);
}

/** 当月だけ出る枠(「こんげつの げんてい」の表示に使う)。 */
export function limitedEntries(gacha: GachaDefinition, now: Date): readonly GachaPoolEntry[] {
  const month = gachaMonth(now);
  return gacha.pool.filter((entry) => entry.months !== undefined && entry.months.includes(month));
}

/** そのガチャが月替わり限定枠を持っているか(ぜんぶの月をまとめて見る)。 */
export function hasLimitedEntries(gacha: GachaDefinition): boolean {
  return gacha.pool.some((entry) => entry.months !== undefined);
}

/** 0以上1未満を返す乱数源。 */
export type Rng = () => number;

export interface PoolEntry {
  readonly itemId: string;
  readonly weight: number;
  readonly rarity: Rarity;
}

export class GachaConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GachaConfigError';
  }
}

function rarityRank(rarity: Rarity): number {
  return RARITY_ORDER.indexOf(rarity);
}

/**
 * ガチャ定義とカタログからレアリティ付きプールを構築する。
 *
 * 月限定枠(docs/12 §3)は**当月ぶんだけ**入る。サーバーの `buildPool` と同じ規則。
 */
export function buildPool(
  gacha: GachaDefinition,
  getItem: (itemId: string) => CatalogItem | undefined,
  now: Date = new Date(),
): PoolEntry[] {
  const month = gachaMonth(now);
  return gacha.pool
    .filter((entry) => isEntryInMonth(entry, month))
    .map((entry) => {
      const item = getItem(entry.itemId);
      if (!item) throw new GachaConfigError(`アイテム ${entry.itemId} がカタログにありません`);
      return { itemId: entry.itemId, weight: entry.weight, rarity: item.rarity };
    });
}

/** プールに実体があり、かつ minRarity 以上のレアリティを昇順で返す。 */
function availableRarities(pool: readonly PoolEntry[], minRarity: Rarity): Rarity[] {
  const min = rarityRank(minRarity);
  return RARITY_ORDER.filter(
    (rarity) => rarityRank(rarity) >= min && pool.some((entry) => entry.rarity === rarity),
  );
}

/** レアリティ抽選。候補の排出率を正規化して選ぶ。 */
function pickRarity(
  pool: readonly PoolEntry[],
  rates: Readonly<Record<Rarity, number>>,
  rng: Rng,
  minRarity: Rarity,
): Rarity {
  const candidates = availableRarities(pool, minRarity);
  if (candidates.length === 0) {
    throw new GachaConfigError(`${minRarity} 以上のアイテムがプールにありません`);
  }
  const total = candidates.reduce((sum, rarity) => sum + rates[rarity], 0);
  let threshold = rng() * total;
  for (const rarity of candidates) {
    threshold -= rates[rarity];
    if (threshold < 0) return rarity;
  }
  // 浮動小数の丸め対策。
  return candidates[candidates.length - 1] as Rarity;
}

/** 同一レアリティ内での重み付き抽選。 */
function pickFromRarity(pool: readonly PoolEntry[], rarity: Rarity, rng: Rng): PoolEntry {
  const entries = pool.filter((entry) => entry.rarity === rarity);
  const total = entries.reduce((sum, entry) => sum + entry.weight, 0);
  let threshold = rng() * total;
  for (const entry of entries) {
    threshold -= entry.weight;
    if (threshold < 0) return entry;
  }
  return entries[entries.length - 1] as PoolEntry;
}

/** 1回ぶんの抽選。minRarity を指定すると保証枠として使える。 */
export function drawOne(
  pool: readonly PoolEntry[],
  rates: Readonly<Record<Rarity, number>>,
  rng: Rng,
  minRarity: Rarity = 'N',
): PoolEntry {
  const rarity = pickRarity(pool, rates, rng, minRarity);
  return pickFromRarity(pool, rarity, rng);
}

/** count 回ぶん抽選する。連ガチャは保証枠を適用する。 */
export function drawMany(
  pool: readonly PoolEntry[],
  gacha: GachaDefinition,
  count: number,
  rng: Rng,
): PoolEntry[] {
  const results: PoolEntry[] = [];
  for (let i = 0; i < count; i += 1) {
    results.push(drawOne(pool, gacha.rarityRates, rng));
  }
  if (count >= gacha.guarantee.count) {
    const guaranteedRank = rarityRank(gacha.guarantee.minRarity);
    const hasRare = results.some((entry) => rarityRank(entry.rarity) >= guaranteedRank);
    if (!hasRare) {
      results[results.length - 1] = drawOne(
        pool,
        gacha.rarityRates,
        rng,
        gacha.guarantee.minRarity,
      );
    }
  }
  return results;
}

/** 抽選結果の1件(UI表示用に名前・絵文字まで解決済み)。 */
export interface DrawnItem {
  readonly itemId: string;
  readonly slot: CatalogItem['slot'];
  readonly rarity: Rarity;
  readonly name: string;
  readonly emoji: string;
  readonly image?: string;
  /** 新規獲得なら true。false は重複でコイン変換されたもの。 */
  readonly isNew: boolean;
  /** 重複時の変換コイン(新規なら 0)。 */
  readonly convertedCoins: number;
}

export interface ResolvedDraw {
  readonly items: readonly DrawnItem[];
  readonly convertedCoins: number;
  /** 新規獲得したアイテムID(インベントリへの書込対象)。 */
  readonly newItemIds: readonly string[];
}

/**
 * 抽選結果を「新規 / 重複(コイン変換)」に振り分ける。
 * 同一ガチャ内の重複も2個目以降は変換対象になる。
 */
export function resolveDraw(
  drawn: readonly PoolEntry[],
  owned: ReadonlySet<string>,
  getItem: (itemId: string) => CatalogItem | undefined,
  convertCoins: Readonly<Record<Rarity, number>>,
): ResolvedDraw {
  const acquired = new Set(owned);
  const items: DrawnItem[] = [];
  const newItemIds: string[] = [];
  let convertedCoins = 0;

  for (const entry of drawn) {
    const item = getItem(entry.itemId);
    if (!item) throw new GachaConfigError(`アイテム ${entry.itemId} がカタログにありません`);
    const isNew = !acquired.has(item.id);
    const coins = isNew ? 0 : (convertCoins[item.rarity] ?? 0);
    if (isNew) {
      acquired.add(item.id);
      newItemIds.push(item.id);
    } else {
      convertedCoins += coins;
    }
    items.push({
      itemId: item.id,
      slot: item.slot,
      rarity: item.rarity,
      name: item.name,
      emoji: item.emoji,
      ...(item.image === undefined ? {} : { image: item.image }),
      isNew,
      convertedCoins: coins,
    });
  }

  return { items, convertedCoins, newItemIds };
}

/** 引く回数ぶんのコスト。連数ちょうどなら cost10、それ以外は単価×回数。 */
export function gachaCost(gacha: GachaDefinition, count: number): number {
  return count === gacha.guarantee.count ? gacha.cost10 : gacha.cost * count;
}

/** UI に出せる連数(単発と連ガチャ)。 */
export function pullCounts(gacha: GachaDefinition): readonly number[] {
  return [1, gacha.guarantee.count];
}

/** 開催期間の判定。from/to が null なら無期限。 */
export function isGachaOpen(gacha: GachaDefinition, now: Date): boolean {
  const { from, to } = gacha.period;
  if (from !== null && now.getTime() < Date.parse(from)) return false;
  if (to !== null && now.getTime() > Date.parse(to)) return false;
  return true;
}

/** 排出率の表示用リスト(法令対応。必ず画面に出す)。 */
export function rarityRateRows(
  gacha: GachaDefinition,
  pool: readonly PoolEntry[],
): readonly { readonly rarity: Rarity; readonly percent: number; readonly count: number }[] {
  return RARITY_ORDER.map((rarity) => ({
    rarity,
    // 0.045 * 100 のような浮動小数の誤差を落とす(表示用なので小数第2位で十分)。
    percent: Math.round(gacha.rarityRates[rarity] * 10000) / 100,
    count: pool.filter((entry) => entry.rarity === rarity).length,
  })).filter((row) => row.count > 0);
}
